/* eslint-disable @eslint-react/dom/no-missing-iframe-sandbox */
/* eslint-disable @eslint-react/dom/no-dangerously-set-innerhtml */
import classNames from 'classnames';
import { ReactNode, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentDocument } from '../hooks/useDocument';

import { useSite } from '../contexts/SiteContext';
import materialIcons from '../materialIcons';
import MetadataRoutes from './MetadataRoutes';

const defaultFontsToLoad = [
    'static/media/Agrandir-WideBold.woff2',
    'static/media/Agrandir-TightRegular.woff2',
    'static/media/Agrandir-TightMedium.woff2',
    'static/media/GarageGothic.woff2',
];

function findAssets(assets, names, extension = 'js') {
    return Object.keys(assets)
        .filter(
            (key) =>
                names.findIndex(
                    (name) =>
                        key.split('.')[0] === name &&
                        key.match(new RegExp(`\.${extension}$`)) !== null,
                ) !== -1,
        )
        .map((key) => assets[key]);
}

export interface HtmlProps {
    assetsManifest: {
        files: Record<string, string>;
        entrypoints?: string[];
    };
    embeddedStyles?: string[] | null;
    embeddedScripts?: string | null;
    tagManagerId?: string;
    bncDCId?: string;
    fontsToLoad?: string[];
    facebookAppId?: string | null;
    loadVendors?: boolean;
    presentation?: Presentation | null;
    children: ReactNode;
}

function Html({
    assetsManifest,
    embeddedStyles = null,
    embeddedScripts = null,
    tagManagerId = null,
    bncDCId = null,
    facebookAppId = null,
    fontsToLoad = defaultFontsToLoad,
    loadVendors = true,
    presentation = null,
    children,
}: HtmlProps) {
    const { handle: siteHandle } = useSite();
    const { locale } = useIntl();
    const { files: assetsFiles, entrypoints = [] } = assetsManifest;
    const {
        type: documentType = null,
        theme: documentTheme = null,
        blocks = [],
        component = null,
    } = useCurrentDocument() || {};
    const { name: themeName = null } = documentTheme || {};
    const interactiveBlocksNames = (blocks || [])
        .filter(({ type }) => type === 'interactive')
        .map(({ name = null }: InteractiveBlock) => name);
    const animationBlocksNames = (blocks || [])
        .filter(({ type }) => type === 'animation')
        .map(({ name = null }: AnimationBlock) => name);

    const assetsNames = [
        documentType !== null ? `document_${documentType}` : null,
        documentType === 'micromag' ? 'micromag' : null,
        themeName !== null ? `theme_${themeName}` : null,
        ...interactiveBlocksNames.reduce(
            (all, name) => [
                ...all,
                `interactive_${name.replace(/[-_]/, '-')}`,
                `interactive_${name.replace(/[-_]/, '_')}`,
            ],
            [],
        ),
        ...animationBlocksNames.reduce(
            (all, name) => [
                ...all,
                `animation_${name.replace(/[-_]/, '-')}`,
                `animation_${name.replace(/[-_]/, '_')}`,
            ],
            [],
        ),
        documentType === 'interactive' && component !== null ? `interactive_${component}` : null,
    ].filter((it) => it !== null);

    const [loadedStylesheets] = useState(() => findAssets(assetsFiles, assetsNames, 'css'));

    const [loadedScripts] = useState(() => findAssets(assetsFiles, assetsNames));

    return (
        <html lang={locale.split('-')[0]} suppressHydrationWarning>
            <head suppressHydrationWarning>
                <meta charSet="utf-8" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, maximum-scale=1"
                />
                {facebookAppId !== null ? (
                    <meta property="fb:app_id" content={facebookAppId} />
                ) : null}
                <link rel="sitemap" type="application/xml" title="Sitemap" href="/sitemap.xml" />
                <MetadataRoutes />

                <link
                    rel="preconnect"
                    href={
                        siteHandle === 'france'
                            ? 'https://api.urbania.fr'
                            : 'https://api.urbania.ca'
                    }
                />
                <link
                    rel="preconnect"
                    href={
                        siteHandle === 'france'
                            ? 'https://images.urbania.fr'
                            : 'https://images.urbania.ca'
                    }
                />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

                {embeddedStyles !== null
                    ? embeddedStyles.map((it, index) => (
                          <style
                              key={`embedded-styles-${index}`}
                              type="text/css"
                              id={`embedded-styles-${index}`}
                              dangerouslySetInnerHTML={{ __html: it }}
                          />
                      ))
                    : entrypoints
                          .filter((it) => it.match(/\.css/) !== null)
                          .map((file) => (
                              <link
                                  rel="preload"
                                  href={`/${file}`}
                                  key={`preload-${file}`}
                                  as="style"
                                  crossOrigin="anonymous"
                              />
                          ))}

                {loadedStylesheets.map((file) => (
                    <link
                        rel="preload"
                        href={file}
                        key={`preload-${file}`}
                        as="style"
                        crossOrigin="anonymous"
                    />
                ))}

                {embeddedScripts !== null && embeddedScripts.length > 0 ? (
                    <script
                        type="text/javascript"
                        id="embedded-scripts"
                        dangerouslySetInnerHTML={{
                            __html: embeddedScripts,
                        }}
                    />
                ) : null}

                {fontsToLoad.map((font) =>
                    typeof assetsFiles[font] !== 'undefined' ? (
                        <link
                            key={`preload-font-${font}`}
                            rel="preload"
                            href={assetsFiles[font]}
                            as="font"
                            crossOrigin="anonymous"
                        />
                    ) : null,
                )}

                {loadVendors ? (
                    <>
                        {tagManagerId !== null ? (
                            <script
                                async
                                src={`https://www.googletagmanager.com/gtm.js?id=${tagManagerId}`}
                                suppressHydrationWarning
                            />
                        ) : null}
                        <script
                            async
                            src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
                            crossOrigin="anonymous"
                            suppressHydrationWarning
                        />
                        {siteHandle === 'urbania' && bncDCId !== null ? (
                            <script
                                async
                                src={`https://www.googletagmanager.com/gtag/js?id=${bncDCId}&l=bncDataLayer`}
                                suppressHydrationWarning
                            />
                        ) : null}
                    </>
                ) : (
                    <link
                        rel="preload"
                        href="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
                        as="script"
                    />
                )}
            </head>
            <body
                suppressHydrationWarning
                className={classNames({ capture: presentation === 'capture' })}
            >
                {tagManagerId !== null ? (
                    <noscript>
                        <iframe
                            src={`https://www.googletagmanager.com/ns.html?id=${tagManagerId}`}
                            title="GTM"
                            height="0"
                            width="0"
                            style={{ display: 'none', visibility: 'hidden' }}
                        />
                    </noscript>
                ) : null}
                {children}

                {loadedScripts.map((file) => (
                    <script key={`script-${file}`} type="text/javascript" src={file} async />
                ))}

                {embeddedStyles === null
                    ? entrypoints
                          .filter((it) => it.match(/\.css/) !== null)
                          .map((file) => (
                              <link rel="stylesheet" href={`/${file}`} key={`stylesheet-${file}`} />
                          ))
                    : null}

                {loadedStylesheets.map((file) => (
                    <link
                        key={`stylesheet-${file}`}
                        rel="stylesheet"
                        href={file}
                        type="text/css"
                        crossOrigin="anonymous"
                    />
                ))}

                <link
                    rel="stylesheet"
                    href={`https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20,600,0.5,0${materialIcons.length > 0 ? `&icon_names=${materialIcons.join(',')}` : ''}&display=block`}
                />
            </body>
        </html>
    );
}

export default Html;
